"use client"

import type { Omit } from "@wagmi/core/internal"
import { type Config, useChainId, useConfig } from "wagmi"

import { Evaluate } from "viem"
import type { GetPriceErrorType } from "../actions/getPrice"
import {
  type GetPriceData,
  type GetPriceOptions,
  type GetPriceQueryFnData,
  type GetPriceQueryKey,
  getPriceQueryOptions,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UsePriceParameters<
  config extends Config = Config,
  selectData = GetPriceData,
> = Evaluate<
  GetPriceOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetPriceQueryFnData,
                GetPriceErrorType,
                selectData,
                GetPriceQueryKey
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
      select?: (data: GetPriceQueryFnData) => selectData
    }
>

export type UsePriceReturnType<selectData = GetPriceData,> = UseQueryReturnType<
  selectData,
  GetPriceErrorType
>

export function usePrice<
  config extends ExtendedConfig,
  selectData = GetPriceData,
>(
  parameters: UsePriceParameters<config, selectData> = {},
): UsePriceReturnType<selectData> {
  const { query = {}, token, scopeKey } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getPriceQueryOptions(config, {
    token,
    chainId: parameters.chainId ?? chainId,
    scopeKey,
  })
  const enabled = Boolean(token && (query.enabled ?? true))

  return useQuery({
    ...query,
    ...options,
    queryKey,
    enabled,
    refetchOnMount: false,
    retry: false,
  })
}
